import { configureStore, combineReducers } from '@reduxjs/toolkit';
import undoable, { excludeAction, groupByActionTypes } from 'redux-undo';
import {
  persistStore, persistReducer, createMigrate, createTransform,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import toolbarReducer from './features/toolbarSlice';
import networkReducer, { initialNetworkState } from './features/networkSlice';
import networkUndoReducer, { setMeta, setNodePositions } from './features/networkUndoSlice';
import { normalizeModel } from './lib/model';
import { PHYSICS_DEFAULTS } from './lib/network/constants';

const UNDO_LIMIT = 20;

// history object as redux-undo builds it, with nothing to undo or redo
const freshHistory = (present) => ({
  past: [],
  present,
  future: [],
  group: null,
  _latestUnfiltered: present,
  index: 0,
  limit: 1,
});

const reducers = combineReducers({
  networkUndo: undoable(networkUndoReducer, {
    limit: UNDO_LIMIT,
    filter: excludeAction([setNodePositions.type]), // layout auto-saves are not user edits
    groupBy: groupByActionTypes([setMeta.type]), // typing in the Meta form is one undo step
  }),
  network: networkReducer,
  toolbar: toolbarReducer,
});

// Only the current model is worth keeping between sessions; undo history can hold twenty
// copies of every image in the model and quickly exceeds the storage quota.
const presentOnly = createTransform(
  (inbound) => ({ present: inbound.present }),
  (outbound) => freshHistory(outbound.present),
  { whitelist: ['networkUndo'] },
);

// Report overlay from older sessions is not part of the builder; drop it on save and load.
const dropOverlay = (network) => {
  if (!network) return network;
  const next = { ...network };
  delete next.overlay;
  return next;
};

const withoutOverlay = createTransform(
  dropOverlay,
  dropOverlay,
  { whitelist: ['network'] },
);

const stripLegacyAnalysis = (state) => {
  if (!state) return state;
  const next = { ...state, network: { ...(state.network || {}) } };
  delete next.analysis;
  delete next.network.servers;
  delete next.network.selectedServer;
  return next;
};

const migrations = {
  // v2: vis-network-shaped model -> domain model; servers move out of the undoable slice;
  // physics settings are re-parameterised for the new layout engine.
  2: (state) => {
    if (!state) return state;
    const { servers, selectedServer, ...present } = state.networkUndo?.present || {};
    const network = state.network || {};
    return {
      ...state,
      networkUndo: freshHistory(normalizeModel(present)),
      network: {
        ...initialNetworkState,
        physics: Boolean(network.physics),
        selfReference: network.selfReference !== false,
        snapToGrid: Boolean(network.snapToGrid),
        offsetPairs: network.offsetPairs !== false,
        displayMode: network.displayMode === 'id' ? 'id' : 'label',
        defaultNodeShape: network.defaultNodeShape || initialNetworkState.defaultNodeShape,
        physicsSettings: { ...PHYSICS_DEFAULTS },
      },
    };
  },
  // v3: re-normalize present (pair flatten was dropped; curves and offset now coexist).
  3: (state) => {
    if (!state) return state;
    const present = state.networkUndo?.present || {};
    return {
      ...state,
      networkUndo: freshHistory(normalizeModel(present)),
    };
  },
  // v4-v6: discard leftover analysis state and server choices from older sessions.
  4: stripLegacyAnalysis,
  5: stripLegacyAnalysis,
  6: stripLegacyAnalysis,
};

const persistConfig = {
  key: 'root',
  version: 6,
  storage,
  transforms: [presentOnly, withoutOverlay],
  migrate: createMigrate(migrations, { debug: false }),
};
const persistedReducer = persistReducer(persistConfig, reducers);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }),
});

export const persistor = persistStore(store);

export default store;
